import { useEffect, useState } from 'react';
import { useInView } from 'motion/react';
import { useRef } from 'react';

interface LiveCounterProps {
  value: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
  className?: string;
  animate?: boolean;
  startValue?: number;
}

export function LiveCounter({
  value,
  duration = 2,
  suffix = '',
  prefix = '',
  className = '',
  animate = false,
  startValue = value,
}: LiveCounterProps) {
  const [count, setCount] = useState(value);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!animate) {
      setCount(value);
      return;
    }

    if (!isInView) return;

    let start = startValue;
    const end = value;
    const totalFrames = Math.max(1, duration * 60);
    const increment = (end - start) / totalFrames;

    setCount(start);

    const timer = setInterval(() => {
      start += increment;
      if ((increment >= 0 && start >= end) || (increment < 0 && start <= end)) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / 60);

    return () => clearInterval(timer);
  }, [animate, isInView, value, duration, startValue]);

  return (
    <span ref={ref} className={className}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
}
